import { create } from 'zustand';

/**
 * Tiny UI-level store for transient, non-document state (toasts today; more
 * later if needed). Lives separately from the editor.store so it doesn't get
 * tangled with the slide-tree state machine or the undo history.
 */

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  message: string;
  kind: ToastKind;
  /** Optional secondary line shown smaller below the main message. */
  detail?: string;
  /** ms to auto-dismiss. Default 4500. Set 0 to require manual dismiss. */
  durationMs?: number;
}

interface UiState {
  toasts: Toast[];
  pushToast(message: string, opts?: Partial<Omit<Toast, 'id' | 'message'>>): string;
  dismissToast(id: string): void;
}

export const useUiStore = create<UiState>((set, get) => ({
  toasts: [],
  pushToast(message, opts) {
    const id = `toast-${Math.random().toString(36).slice(2, 9)}`;
    const toast: Toast = {
      id,
      message,
      kind: opts?.kind ?? 'info',
      detail: opts?.detail,
      durationMs: opts?.durationMs ?? 4500,
    };
    set({ toasts: [...get().toasts, toast] });
    if (toast.durationMs && toast.durationMs > 0) {
      setTimeout(() => get().dismissToast(id), toast.durationMs);
    }
    return id;
  },
  dismissToast(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
}));

/** Convenience for non-React callers (drop handlers, store actions). */
export const pushToast = (
  message: string,
  opts?: Partial<Omit<Toast, 'id' | 'message'>>,
) => useUiStore.getState().pushToast(message, opts);
